/**
 * Download the update APK to the device, then hand it to the system installer.
 *
 * The download reuses the same native path the chart packs use:
 * Filesystem.downloadFile goes through the OS HTTP stack (not the WebView fetch),
 * so it steps around the CORS + range wall that blocks the WebView from reading
 * GitHub's release-asset host directly. The APK lands in the app's CACHE dir under
 * updates/ — cache (not Data) because our FileProvider already exposes the cache
 * dir (res/xml/file_paths.xml <cache-path>), which is what lets us build the
 * content:// URI the installer needs, and because a one-shot installer file has no
 * reason to persist.
 *
 * This module is native-only by design; callers guard with canUpdate() so it's
 * never reached on web/dev.
 */
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { ApkInstaller } from './apkInstaller';

const DIR = Directory.Cache;
const APK_DIR = 'updates';
/** One fixed name — we only ever keep the latest downloaded update. */
const APK_PATH = `${APK_DIR}/ardent-fix-update.apk`;

export interface ApkDownloadProgress {
  /** Bytes written so far. */
  bytes: number;
  /** Total bytes (0 if the host didn't send a content-length). */
  total: number;
}

/**
 * Download the APK at `url` to the cache dir, reporting byte progress, and return
 * the on-device file URI. Any previous download is deleted first so a stale or
 * half-written file can never be the one we install. Throws on failure.
 */
export async function downloadApk(
  url: string,
  onProgress?: (p: ApkDownloadProgress) => void,
): Promise<string> {
  await Filesystem.mkdir({ path: APK_DIR, directory: DIR, recursive: true }).catch(() => {
    /* already exists — fine */
  });
  await Filesystem.deleteFile({ path: APK_PATH, directory: DIR }).catch(() => {
    /* nothing to clear */
  });

  let handle: PluginListenerHandle | undefined;
  if (onProgress) {
    handle = await Filesystem.addListener('progress', (p) => {
      onProgress({ bytes: p.bytes, total: p.contentLength });
    });
  }
  try {
    await Filesystem.downloadFile({
      url,
      path: APK_PATH,
      directory: DIR,
      progress: true,
      recursive: true,
    });
  } finally {
    await handle?.remove();
  }

  const { uri } = await Filesystem.getUri({ path: APK_PATH, directory: DIR });
  return uri;
}

/** Download the APK and launch the system installer over the top of the app. */
export async function downloadAndInstallApk(
  url: string,
  onProgress?: (p: ApkDownloadProgress) => void,
): Promise<void> {
  const uri = await downloadApk(url, onProgress);
  await ApkInstaller.install({ path: uri });
}

/** Whether native APK install is usable here (a real Android/Capacitor platform). */
export function canInstallApk(): boolean {
  return Capacitor.isNativePlatform();
}
